import React, { useMemo, useState } from "react";
import {
  Box, Breadcrumbs, Button, Card, FormControl, Grid, InputAdornment,
  Link as MLink, MenuItem, Menu, Select, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarIcon from "@mui/icons-material/Star";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { CiSearch } from "react-icons/ci";
import { Link, useNavigate } from "react-router-dom";
import ObjectiveChooserDialog from "../shared/ObjectiveChooserDialog";
import {
  useListMasteryWorkflowsQuery, useDuplicateMasteryWorkflowMutation,
  useSetDefaultMasteryWorkflowMutation, useArchiveMasteryWorkflowMutation,
  useRestoreMasteryWorkflowMutation, useDeleteMasteryWorkflowMutation,
} from "../../../../Api/attoDeskApi";
import { useNotifier } from "../../../../Core/Notifier";
import useDebounce from "../../../../Components/SearchDebonce/useDebounce";
import DeletePopup from "../../../../Components/Delete/DeletePopup";
import { LegacyTokens, TypeScale, PageSurface, B13, B13Ink, tint12 } from "../../../../theme/b13Tokens";
import {
  AbaFilterSelect, AbaActionCell, AbaRowIconButton, AbaStatusCell, AbaUpdatedCell, formatAbaDate,
  ABA_TABLE_STATUS_WIDTH, ABA_TABLE_UPDATED_WIDTH, ABA_TABLE_ACTION_WIDTH, ABA_TABLE_DEFAULT_WIDTH,
} from "../../../ProgramLibrary/shared/abaTable";

/**
 * Workflows workspace list.
 *
 * Batch 39 — visual redesign only. All RTK-Query hooks, mutations,
 * filter state (search/status/page/perPage), row actions, delete
 * dialog, and menu handlers are preserved verbatim.
 */
export default function MasteryWorkflowsList() {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const debounced = useDebounce(search, 300);

  const { data, isFetching, refetch } = useListMasteryWorkflowsQuery({
    searchText: debounced, status: status || null, page: page + 1, perPage,
  });
  const rows: any[] = Array.isArray(data?.data) ? (data!.data as any[]) : [];
  const total: number = Number((data as any)?.total ?? 0);
  const lastPage = Math.max(1, Math.ceil(total / perPage));

  const [duplicate] = useDuplicateMasteryWorkflowMutation();
  const [setDefault] = useSetDefaultMasteryWorkflowMutation();
  const [archive] = useArchiveMasteryWorkflowMutation();
  const [restore] = useRestoreMasteryWorkflowMutation();
  const [remove] = useDeleteMasteryWorkflowMutation();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);
  const [deleteRow, setDeleteRow] = useState<any>(null);

  // T63 Part J — Add Workflow now opens the objective chooser first.
  // The chooser navigates to /workflows/new?objective=<key> only after
  // the operator picks one; the editor rejects the plain /new route.
  const [chooserOpen, setChooserOpen] = useState(false);
  const openObjectiveChooser = () => setChooserOpen(true);
  const closeObjectiveChooser = () => setChooserOpen(false);

  const call = async (fn: () => Promise<any>, ok: string) => {
    try {
      const r = await fn();
      if (r?.status) { showMessage(r.message || ok); refetch(); }
      else showErrorMessage(r?.message || "Action failed");
    } catch { showErrorMessage("Action failed"); }
  };

  // Batch 39 — summary strip is computed from the CURRENT PAGE's rows +
  // the API-reported total. No new endpoint. The Programs/Targets figures
  // are exact for the current page and understated for large libraries;
  // the caption on the strip flags that honestly rather than pretending
  // to be a global total the API doesn't return.
  const summary = useMemo(() => {
    const active = rows.filter((r) => (r.status || "active") === "active").length;
    const hasDefault = rows.some((r) => !!r.isDefault);
    const programsSum = rows.reduce((s, r) => s + (r.programsUsing ?? 0), 0);
    const targetsSum  = rows.reduce((s, r) => s + (r.targetsUsing ?? 0), 0);
    return { total, active, hasDefault, programsSum, targetsSum };
  }, [rows, total]);
  const showSummary = !isFetching && total > 0;
  const anyFilter = !!(search || status);

  return (
    <Box sx={{ ...PageSurface, p: { xs: 2, md: 3.5 } }}>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }} separator="/">
          <MLink underline="hover" component={Link} to="/aba-therapy/programs" sx={{ color: B13Ink.ink3 }}>ABA Therapy</MLink>
          <Typography sx={{ fontSize: 12, color: LegacyTokens.ink }}>Workflows</Typography>
        </Breadcrumbs>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: { md: "flex-start" }, justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minWidth: 0 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
              backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <AccountTreeOutlinedIcon fontSize="medium" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>
                  Workflows
                </Typography>
                <Box sx={{
                  display: "inline-flex", alignItems: "center",
                  backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  px: 1, py: 0.25, borderRadius: `${LegacyTokens.radius.chip}px`,
                }}>Clinical Progression Rules</Box>
              </Box>
              <Typography sx={{ ...TypeScale.caption, mt: 0.5, maxWidth: 720 }}>
                Phase-based progression rules — from Baseline through Maintenance — that decide when a Target has been mastered.
              </Typography>
            </Box>
          </Box>
          {/* T63 Part O — single Add Workflow entry point. The old
              "Use standard workflow" button was redundant with the
              objective chooser which now always produces the
              standard Probe/Acquisition/Mastered/Waiting structure
              (see createStandardWorkflowForObjective factory).
              Removed here so there is one clear action. */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
            <Button
              variant="contained" size="medium"
              startIcon={<AddIcon fontSize="small" />}
              onClick={openObjectiveChooser}
              sx={{
                backgroundColor: B13.primary, color: LegacyTokens.surface,
                fontWeight: 600, textTransform: "none",
                px: 2, py: 1, borderRadius: `${LegacyTokens.radius.control}px`,
                boxShadow: "0 1px 2px rgba(15, 61, 46, 0.15)",
                "&:hover": { backgroundColor: B13.primary, boxShadow: "0 2px 6px rgba(15, 61, 46, 0.25)" },
              }}
            >
              Add Workflow
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ── Summary strip (from real data only) ────────────────────── */}
      {showSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }} data-testid="workflows-summary-strip">
          <SummaryCard label="Total Workflows" value={total} icon={<AccountTreeOutlinedIcon fontSize="small" />} accent={LegacyTokens.purple} tile={LegacyTokens.purpleSoft} />
          <SummaryCard label="Active on page" value={summary.active} icon={<CheckCircleOutlineIcon fontSize="small" />} accent={LegacyTokens.teal} tile={LegacyTokens.tealSoft} />
          <SummaryCard label="Default set" value={summary.hasDefault ? "Yes" : "—"} icon={<StarIcon fontSize="small" />} accent={LegacyTokens.emerald} tile={LegacyTokens.emeraldSoft} />
          <SummaryCard label="Programs Using" value={summary.programsSum} icon={<PeopleAltOutlinedIcon fontSize="small" />} accent={LegacyTokens.blue} tile={LegacyTokens.blueSoft} pageScoped />
          <SummaryCard label="Targets Using" value={summary.targetsSum} icon={<PlaylistAddCheckIcon fontSize="small" />} accent={LegacyTokens.amber} tile={LegacyTokens.amberSoft} pageScoped />
        </Grid>
      )}

      {/* ── Search + filter toolbar ────────────────────────────────── */}
      <Card variant="outlined" sx={{
        p: 2, mb: 2,
        backgroundColor: LegacyTokens.surface,
        borderColor: LegacyTokens.line,
        borderRadius: `${LegacyTokens.radius.card}px`,
        boxShadow: "none",
      }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={anyFilter ? 7 : 9}>
            <TextField size="small" fullWidth label="Search" placeholder="Search Workflows" value={search}
              onChange={(e) => { setPage(0); setSearch(e.target.value); }}
              sx={{
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: LegacyTokens.surface, height: 44,
                  borderRadius: `${LegacyTokens.radius.control}px`,
                  "& fieldset": { borderColor: LegacyTokens.line },
                  "&:hover fieldset": { borderColor: B13.primary },
                  "&.Mui-focused fieldset": { borderColor: B13.primary },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: B13Ink.ink3 }} /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AbaFilterSelect
              label="Status"
              value={status}
              emptyLabel="All statuses"
              onChange={(v) => { setPage(0); setStatus(v); }}
              fullWidth
              options={[
                { value: "draft", label: "Draft" },
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" },
              ]}
            />
          </Grid>
          {anyFilter && (
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                onClick={() => { setSearch(""); setStatus(""); setPage(0); }}
                sx={{
                  color: B13.primary, textTransform: "none", fontWeight: 600,
                  border: `1px solid ${tint12(B13.primary)}`,
                  height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
                  "&:hover": { backgroundColor: tint12(B13.primary) },
                }}>Clear filters</Button>
            </Grid>
          )}
        </Grid>
      </Card>

      {/* ── Table card ──────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{
        overflow: "hidden",
        backgroundColor: LegacyTokens.surface,
        borderColor: LegacyTokens.line,
        borderRadius: `${LegacyTokens.radius.card}px`,
        boxShadow: "none",
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{
            minWidth: 1120,
            tableLayout: "fixed",
            "& td, & th": { borderBottomColor: LegacyTokens.line, py: 1.5, px: 2 },
          }}>
            <colgroup>
              <col style={{ minWidth: 360 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 160 }} />
              <col style={{ width: ABA_TABLE_DEFAULT_WIDTH }} />
              <col style={{ width: ABA_TABLE_STATUS_WIDTH }} />
              <col style={{ width: ABA_TABLE_UPDATED_WIDTH }} />
              <col style={{ width: ABA_TABLE_ACTION_WIDTH }} />
            </colgroup>
            <TableHead>
              <TableRow sx={{ backgroundColor: B13.canvas, "& th": { height: 48 } }}>
                <TableCell sx={{ ...TypeScale.tableHeader }}>Workflow Name</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }}>Objective</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }} align="center">Phases</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }}>Programs / Targets</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }} align="center">Default</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }} align="center">Status</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }}>Updated</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <TableCell key={j} sx={{ py: 2.25 }}>
                        <Box sx={{ height: 14, borderRadius: 0.5, backgroundColor: B13.canvas, opacity: 0.6 }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      display: "inline-flex", width: 48, height: 48, borderRadius: `${LegacyTokens.radius.control}px`,
                      backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
                      alignItems: "center", justifyContent: "center", mb: 2,
                    }}>
                      <AccountTreeOutlinedIcon />
                    </Box>
                    <Typography sx={{ ...TypeScale.sectionHead, mb: 0.5 }}>
                      {anyFilter ? "No Workflows match your filters." : "Create your first Workflow"}
                    </Typography>
                    <Typography sx={{ ...TypeScale.caption, maxWidth: 460, mx: "auto", mb: 2.5 }}>
                      {anyFilter
                        ? "Try clearing the search or status filter, or create a new Workflow."
                        : "Define how ABA Targets progress from Baseline through Acquisition, Generalization, Mastery and Maintenance."}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                      {anyFilter ? (
                        <Button size="small"
                          onClick={() => { setSearch(""); setStatus(""); setPage(0); }}
                          sx={{
                            color: B13.primary, textTransform: "none", fontWeight: 600,
                            border: `1px solid ${tint12(B13.primary)}`,
                            "&:hover": { backgroundColor: tint12(B13.primary) },
                          }}>Clear filters</Button>
                      ) : null}
                      <Button size="small" variant="contained" startIcon={<AddIcon fontSize="small" />}
                        onClick={openObjectiveChooser}
                        sx={{
                          backgroundColor: B13.primary, color: LegacyTokens.surface,
                          textTransform: "none", fontWeight: 600,
                          "&:hover": { backgroundColor: B13.primary },
                        }}>Add Workflow</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover sx={{
                  "&:hover": { backgroundColor: LegacyTokens.canvas },
                }}>
                  <TableCell sx={{ py: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{
                        width: 36, height: 36, borderRadius: `${LegacyTokens.radius.control}px`,
                        backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.25,
                      }}>
                        <AccountTreeOutlinedIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <MLink component={Link} to={`/aba-therapy/workflows/${r.id}`} underline="hover" sx={{ color: LegacyTokens.ink }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: LegacyTokens.ink }}>{r.workflowName}</Typography>
                        </MLink>
                        {r.description && (
                          <Typography sx={{ ...TypeScale.caption, mb: 0.75 }} title={r.description}>
                            {r.description}
                          </Typography>
                        )}
                        <WorkflowFlow phases={r.phases} />
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <ObjectiveChip value={r.objective} />
                  </TableCell>
                  <TableCell align="center">
                    <PhaseCountBadge count={r.phaseCount ?? 0} />
                  </TableCell>
                  <TableCell>
                    <UsageCell programs={r.programsUsing ?? 0} targets={r.targetsUsing ?? 0} />
                  </TableCell>
                  <TableCell align="center">
                    {r.isDefault ? <DefaultBadge /> : <Box component="span" sx={{ color: B13Ink.ink3 }}>—</Box>}
                  </TableCell>
                  <TableCell align="center">
                    <AbaStatusCell>
                      <Box sx={{ mx: "auto" }}>
                        <WorkflowStatusChip status={r.status || "active"} />
                      </Box>
                    </AbaStatusCell>
                  </TableCell>
                  <TableCell>
                    <AbaUpdatedCell
                      icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: B13Ink.ink3 }} />}
                      value={r.updated_at ? formatAbaDate(r.updated_at) : "—"}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ px: 1.5 }}>
                    <AbaActionCell>
                      <AbaRowIconButton title="View Workflow" onClick={() => navigate(`/aba-therapy/workflows/${r.id}`)}>
                        <VisibilityIcon fontSize="small" />
                      </AbaRowIconButton>
                      <AbaRowIconButton title="Edit Workflow" onClick={() => navigate(`/aba-therapy/workflows/${r.id}/edit`)}>
                        <EditIcon fontSize="small" />
                      </AbaRowIconButton>
                      <AbaRowIconButton title="More actions" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(r); }}>
                        <MoreVertIcon fontSize="small" />
                      </AbaRowIconButton>
                    </AbaActionCell>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* ── Pagination footer ────────────────────────────────────── */}
        <Box sx={{
          borderTop: `1px solid ${LegacyTokens.line}`,
          backgroundColor: B13.canvas,
          p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 1,
        }}>
          <Typography sx={{ ...TypeScale.caption }}>
            {total === 0
              ? "0 Workflows"
              : (() => {
                  const from = Math.min(total, page * perPage + 1);
                  const to = Math.min(total, (page + 1) * perPage);
                  return `Showing ${from}–${to} of ${total} Workflow${total === 1 ? "" : "s"}`;
                })()}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ ...TypeScale.caption }}>Rows</Typography>
            <FormControl size="small">
              <Select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
                sx={{
                  minWidth: 80, height: 36,
                  backgroundColor: LegacyTokens.surface,
                  borderRadius: `${LegacyTokens.radius.control}px`,
                  "& fieldset": { borderColor: LegacyTokens.line },
                }}>
                {[15, 25, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="small" disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              sx={{
                color: B13.primary, textTransform: "none", fontWeight: 600,
                border: `1px solid ${tint12(B13.primary)}`,
                backgroundColor: LegacyTokens.surface,
                "&:hover": { backgroundColor: tint12(B13.primary) },
                "&.Mui-disabled": { color: B13Ink.ink3, borderColor: LegacyTokens.line },
              }}>Prev</Button>
            <Button size="small" disabled={page + 1 >= lastPage || total === 0}
              onClick={() => setPage((p) => p + 1)}
              sx={{
                color: B13.primary, textTransform: "none", fontWeight: 600,
                border: `1px solid ${tint12(B13.primary)}`,
                backgroundColor: LegacyTokens.surface,
                "&:hover": { backgroundColor: tint12(B13.primary) },
                "&.Mui-disabled": { color: B13Ink.ink3, borderColor: LegacyTokens.line },
              }}>Next</Button>
          </Stack>
        </Box>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: `${LegacyTokens.radius.control}px`, minWidth: 200, boxShadow: "0 4px 12px rgba(15, 61, 46, 0.10)" } }}>
        <MenuItem onClick={() => { setMenuAnchor(null); call(() => duplicate(menuRow.id).unwrap(), "Duplicated"); }}>
          <ContentCopyIcon fontSize="small" style={{ marginRight: 8, color: B13Ink.ink3 }} /> Duplicate
        </MenuItem>
        {!menuRow?.isDefault && (
          <MenuItem onClick={() => { setMenuAnchor(null); call(() => setDefault(menuRow.id).unwrap(), "Default updated"); }}>
            <StarIcon fontSize="small" style={{ marginRight: 8, color: LegacyTokens.emerald }} /> Set as default
          </MenuItem>
        )}
        {menuRow?.status !== "archived" ? (
          <MenuItem onClick={() => { setMenuAnchor(null); call(() => archive(menuRow.id).unwrap(), "Archived"); }}>
            <ArchiveIcon fontSize="small" style={{ marginRight: 8, color: B13Ink.ink3 }} /> Archive
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { setMenuAnchor(null); call(() => restore(menuRow.id).unwrap(), "Restored"); }}>
            <UnarchiveIcon fontSize="small" style={{ marginRight: 8, color: B13Ink.ink3 }} /> Restore
          </MenuItem>
        )}
        <MenuItem onClick={() => { setDeleteRow(menuRow); setMenuAnchor(null); }} sx={{ color: "error.main" }}>
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} /> Delete
        </MenuItem>
      </Menu>

      {deleteRow && (
        <DeletePopup
          open
          handleCloseDelete={() => setDeleteRow(null)}
          onConfirm={async () => {
            await call(() => remove(deleteRow.id).unwrap(), "Deleted");
            setDeleteRow(null);
          }}
          title="Delete Workflow"
          content={`Delete "${deleteRow.workflowName}"? This is blocked if the workflow is in use.`}
        />
      )}

      {/* T63 Part J — objective chooser. Opens on Add Workflow /
          "Use standard workflow"; navigates to /new?objective=<key>
          only after the operator picks an objective. */}
      <ObjectiveChooserDialog
        open={chooserOpen}
        onClose={closeObjectiveChooser}
        onSelect={(objective) => {
          closeObjectiveChooser();
          navigate(`/aba-therapy/settings/workflows/new?objective=${encodeURIComponent(objective.key)}`);
        }}
      />
    </Box>
  );
}

// ─── Local components (visual only) ─────────────────────────────────

// One card in the summary strip. Displays a small caption tag when the
// number is scoped to the current page rather than the full library (see
// Programs Using / Targets Using — the list API returns per-page usage
// totals, not global counts, so we don't imply otherwise).
const SummaryCard: React.FC<{
  label: string; value: React.ReactNode; icon: React.ReactNode;
  accent: string; tile: string; pageScoped?: boolean;
}> = ({ label, value, icon, accent, tile, pageScoped }) => (
  <Grid item xs={6} sm={4} md={2.4}>
    <Box sx={{
      height: "100%", p: 2,
      display: "flex", flexDirection: "column", gap: 1.25,
      backgroundColor: LegacyTokens.surface,
      border: `1px solid ${LegacyTokens.line}`,
      borderRadius: `${LegacyTokens.radius.card}px`,
      transition: "box-shadow 150ms",
      "&:hover": { boxShadow: "0 2px 8px rgba(15, 61, 46, 0.06)" },
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: `${LegacyTokens.radius.control}px`,
        backgroundColor: tile, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontSize: 24, fontWeight: 650, color: LegacyTokens.ink, lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mt: 0.5 }}>
          {label}
          {pageScoped && <Box component="span" sx={{ ml: 0.5, textTransform: "none", letterSpacing: 0, fontSize: 10, fontWeight: 500, color: B13Ink.ink3 }}>(on page)</Box>}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

// Batch 13 Part C §2 — compact phase-colour dot strip. Renders ONLY
// from real mastery_workflow_phases rows now returned by listPaged
// (Batch 13 Part C §2 backend). The description-parse fallback that
// used to fabricate phases from a free-text field has been deleted —
// a fallback that invents clinical configuration is worse than an
// empty state.
//
// Anatomy: colour-only dots in orderNumber order, tooltip per dot
// giving the phase name. Missing colour renders as a hairline-
// outlined empty dot so "missing" is visible as missing rather than
// hidden. No inline labels — the row stays dense.
type WorkflowPhase = { id: number; name: string; colour: string | null; orderNumber: number };
const WorkflowFlow: React.FC<{ phases?: WorkflowPhase[] }> = ({ phases }) => {
  const rows = Array.isArray(phases) ? phases : [];
  if (rows.length === 0) {
    return (
      <Typography sx={{ fontSize: 11, color: B13Ink.ink3, fontStyle: "italic", mt: 0.5 }}>
        No phases configured.
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
      aria-label={`Phases: ${rows.map((p) => p.name).join(" → ")}`}>
      {rows.map((p, i) => (
        <React.Fragment key={p.id}>
          <Tooltip title={p.name} arrow>
            <Box component="span" sx={{
              width: 12, height: 12, borderRadius: "50%",
              backgroundColor: p.colour ?? "transparent",
              border: p.colour ? "none" : `1px solid ${B13.line}`,
              flexShrink: 0,
            }} />
          </Tooltip>
          {i < rows.length - 1 && (
            <Box component="span" sx={{ color: B13Ink.ink3, fontSize: 12 }} aria-hidden>→</Box>
          )}
        </React.Fragment>
      ))}
    </Stack>
  );
};

// Batch 13 Part C §1 correction — Objective is free text
// (mastery_workflows.objective is varchar(191), not an enum), so a
// keyword-derived accent would be inference dressed as semantics —
// "increase time on task" would render orange because "time"
// happens to appear in the string. Colour that looks systematic but
// is not carries no information (same rule that killed the tag-hue
// hash in Batch 13 Part B §3).
//
// Every objective chip therefore renders in ONE neutral accent —
// primary-blue tint — until Objective becomes an enumerated field
// with configured colours. Empty value renders "—" in ink3.
const ObjectiveChip: React.FC<{ value?: string | null }> = ({ value }) => {
  const raw = String(value ?? "").trim();
  if (raw === "") return <Box component="span" sx={{ color: B13Ink.ink3 }}>—</Box>;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center",
      backgroundColor: tint12(B13.primary), color: B13.primary,
      borderRadius: `${LegacyTokens.radius.chip}px`,
      fontSize: 12, fontWeight: 600, lineHeight: 1.2,
      px: 1, py: 0.25, maxWidth: "100%",
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    }}>{raw}</Box>
  );
};

const PhaseCountBadge: React.FC<{ count: number }> = ({ count }) => (
  <Box component="span" sx={{
    display: "inline-flex", alignItems: "center", gap: 0.5,
    backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
    borderRadius: `${LegacyTokens.radius.chip}px`,
    fontSize: 12, fontWeight: 600,
    px: 1, py: 0.25,
  }}>
    <LayersOutlinedIcon sx={{ fontSize: 14 }} />
    {count} {count === 1 ? "Phase" : "Phases"}
  </Box>
);

const UsageCell: React.FC<{ programs: number; targets: number }> = ({ programs, targets }) => (
  <Box>
    <Typography sx={{ fontSize: 13, color: LegacyTokens.ink, fontWeight: 500 }}>
      {programs} Program{programs === 1 ? "" : "s"}
    </Typography>
    <Typography sx={{ fontSize: 12, color: B13Ink.ink3 }}>
      {targets} Target{targets === 1 ? "" : "s"}
    </Typography>
  </Box>
);

const DefaultBadge: React.FC = () => (
  <Box component="span" sx={{
    display: "inline-flex", alignItems: "center", gap: 0.5,
    backgroundColor: LegacyTokens.emeraldSoft, color: LegacyTokens.emeraldText,
    borderRadius: `${LegacyTokens.radius.chip}px`,
    fontSize: 12, fontWeight: 600,
    px: 1, py: 0.25,
  }}>
    <StarIcon sx={{ fontSize: 14, color: LegacyTokens.emerald }} />
    Default
  </Box>
);

// Workflow status badge. Backend enums: active / draft / archived.
const WorkflowStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const raw = String(status ?? "active").toLowerCase();
  const map: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    active:   { bg: LegacyTokens.emeraldSoft, text: LegacyTokens.emeraldText, dot: LegacyTokens.emerald, label: "Active" },
    draft:    { bg: LegacyTokens.amberSoft,   text: LegacyTokens.amberText, dot: LegacyTokens.amber,   label: "Draft" },
    archived: { bg: LegacyTokens.neutralSoft, text: LegacyTokens.neutralText, dot: LegacyTokens.neutralDot, label: "Archived" },
  };
  const t = map[raw] ?? map.active;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      backgroundColor: t.bg, color: t.text,
      borderRadius: `${LegacyTokens.radius.chip}px`,
      fontSize: 12, fontWeight: 600,
      px: 1, py: 0.25,
    }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: t.dot }} />
      {t.label}
    </Box>
  );
};

